/**
 * Blind random bot — baseline that should almost always lose if balance is healthy.
 * Uses only the hand; no strategy; often plays without useful aiming.
 */

import { LAYER_ORDER, type LayerId } from "../game/board/types";
import type { DragIntent } from "../game/cards/types";
import type { Game } from "../game/Game";
import type { BotAction } from "./types";

function randInt(n: number): number {
  return Math.floor(Math.random() * n);
}

function pick<T>(arr: T[]): T | undefined {
  if (!arr.length) return undefined;
  return arr[randInt(arr.length)];
}

function randomIntent(): DragIntent {
  const layer = LAYER_ORDER[randInt(LAYER_ORDER.length)] as LayerId;
  return { layer, slot: randInt(3) };
}

export function randomDecide(game: Game): BotAction {
  if (game.status !== "playing") return { type: "wait", reason: "not playing" };
  if (game.paused) return { type: "wait", reason: "paused" };
  if (game.isProclaiming()) return { type: "wait", reason: "proclaiming" };

  const hand = game.hand;
  if (hand.length === 0) {
    return { type: "wait", reason: "no cards" };
  }

  // Prefer longer speech cards (tempo waste) and never smart-target
  const byLen = [...hand].sort(
    (a, b) => (b.def.speakSeconds ?? 2) - (a.def.speakSeconds ?? 2),
  );
  const pool = byLen.slice(0, Math.max(1, Math.ceil(byLen.length * 0.6)));
  const card = pick(pool)!;
  return {
    type: "proclaim",
    instanceId: card.instanceId,
    intent: randomIntent(),
    reason: `random ${card.def.reference}`,
  };
}
